import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import {
  getDRCProfileRequest,
  recordDRCIR35Request,
  setDRCEndUserStatusRequest,
  setDRCPeriodRequest,
  updateDRCProfileRequest,
  verifyDRCCompanyRequest,
} from '@vyce/core/src/api/legend/drc';
import { checkEmployeeVATRequest } from '@vyce/core/src/api/legend/pay';
import { NotificationContext } from '@vyce/core/src/contexts/notificationContext';
import { useBooleanState } from '@vyce/core/src/hooks';
import {
  DRC_REASON_CODES,
  DRCIR35Payload,
  DRCPeriodPayload,
  DRCProfile,
  DRCProfilePayload,
  DRCResponse,
  DRCStatus,
  END_USER_STATUSES,
} from '@vyce/core/src/types';

/** HMRC usually answers within a few seconds; after ~30s the check is left to the next page load. */
const VAT_POLL_INTERVAL_MS = 2000;
const VAT_POLL_MAX_ATTEMPTS = 15;

const isVatUnverified = (status: DRCStatus | null) =>
  status?.reason_code === DRC_REASON_CODES.VAT_NOT_VERIFIED ||
  !!status?.blockers?.includes(DRC_REASON_CODES.VAT_NOT_VERIFIED);

/**
 * The panel owns its own state and its own requests rather than joining the Employment tab's
 * shared form: that form diffs fields shallowly against the employee, which nested DRC objects
 * would either always or never survive.
 */
export const useDRCProfile = (userId?: string) => {
  const { handleServerError, showNotification } = useContext(NotificationContext);
  const [profile, setProfile] = useState<DRCProfile | null>(null);
  const [status, setStatus] = useState<DRCStatus | null>(null);
  const [loading, setLoadingTrue, setLoadingFalse] = useBooleanState(true);
  const [saving, setSavingTrue, setSavingFalse] = useBooleanState(false);
  const [vatChecking, setVatCheckingTrue, setVatCheckingFalse] = useBooleanState(false);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const applyResponse = useCallback((data: DRCResponse) => {
    setProfile(data.profile);
    setStatus(data.status);
  }, []);

  const getProfile = useCallback(async () => {
    if (!userId) return;
    try {
      setLoadingTrue();
      const { data } = await getDRCProfileRequest(userId);
      applyResponse(data);
    } catch (e) {
      handleServerError(e);
    } finally {
      setLoadingFalse();
    }
  }, [userId, applyResponse, handleServerError, setLoadingTrue, setLoadingFalse]);

  const stopVatPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    setVatCheckingFalse();
  }, [setVatCheckingFalse]);

  useEffect(() => {
    getProfile();
    return stopVatPolling;
  }, [getProfile, stopVatPolling]);

  /**
   * Wraps every mutation: one in-flight flag, one error path, and the recomputed status from
   * the response rather than a second read.
   */
  const mutate = useCallback(
    async (request: () => Promise<{ data: DRCResponse }>, successMessage?: string) => {
      if (!userId) return false;
      try {
        setSavingTrue();
        const { data } = await request();
        applyResponse(data);
        if (successMessage) {
          showNotification({ message: successMessage, options: { variant: 'success' } });
        }
        return true;
      } catch (e) {
        handleServerError(e);
        return false;
      } finally {
        setSavingFalse();
      }
    },
    [userId, applyResponse, handleServerError, showNotification, setSavingTrue, setSavingFalse]
  );

  /**
   * Partial save — only what was edited. Changing the VAT number drops the profile back to
   * vat_not_verified, which the recomputed status in the response already reflects.
   */
  const saveProfile = useCallback(
    (payload: DRCProfilePayload) =>
      mutate(() => updateDRCProfileRequest(userId as string, payload), 'VAT and CIS details saved'),
    [mutate, userId]
  );

  const setEndUserStatus = useCallback(
    (status: END_USER_STATUSES, note?: string) =>
      mutate(
        () => setDRCEndUserStatusRequest(userId as string, { status, note }),
        'End-user status recorded'
      ),
    [mutate, userId]
  );

  const recordIR35 = useCallback(
    (payload: DRCIR35Payload) =>
      mutate(() => recordDRCIR35Request(userId as string, payload), 'Employment status check recorded'),
    [mutate, userId]
  );

  const setPeriod = useCallback(
    (payload: DRCPeriodPayload) =>
      mutate(() => setDRCPeriodRequest(userId as string, payload), 'Reverse charge period updated'),
    [mutate, userId]
  );

  /**
   * Re-reads the profile quietly until HMRC's answer lands — no loading flag, so the panel does
   * not flash or remount while the Legend waits. A number HMRC rejects stays unverified, which
   * looks the same as no answer yet, so the poll gives up after a fixed number of reads.
   */
  const pollVatStatus = useCallback(
    (attempt: number) => {
      pollTimerRef.current = setTimeout(async () => {
        if (!userId) return stopVatPolling();
        try {
          const { data } = await getDRCProfileRequest(userId);
          applyResponse(data);
          if (!isVatUnverified(data.status)) return stopVatPolling();
        } catch (e) {
          // A failed read is retried on the next tick rather than surfaced mid-wait.
        }
        if (attempt + 1 >= VAT_POLL_MAX_ATTEMPTS) {
          stopVatPolling();
          showNotification({
            message: "HMRC hasn't answered yet",
            description: 'The status will update next time you open this page.',
            options: { variant: 'warning' },
          });
          return;
        }
        pollVatStatus(attempt + 1);
      }, VAT_POLL_INTERVAL_MS);
    },
    [userId, applyResponse, showNotification, stopVatPolling]
  );

  /**
   * Starts the HMRC check for the number already on the profile. The check is keyed by the pay
   * employee, not the user, and runs in the background — nothing is verified when this returns,
   * so the panel polls the profile until the result shows.
   */
  const verifyVat = useCallback(
    async (employeeId: string, vatNumber: string) => {
      try {
        setSavingTrue();
        await checkEmployeeVATRequest(employeeId, vatNumber);
        stopVatPolling();
        setVatCheckingTrue();
        showNotification({ message: 'Checking with HMRC…', options: { variant: 'success' } });
        pollVatStatus(0);
        return true;
      } catch (e) {
        handleServerError(e);
        return false;
      } finally {
        setSavingFalse();
      }
    },
    [
      handleServerError,
      showNotification,
      setSavingTrue,
      setSavingFalse,
      setVatCheckingTrue,
      stopVatPolling,
      pollVatStatus,
    ]
  );

  /**
   * Resolves to the stored Companies House snapshot, or null. 404 is a wrong number rather than
   * a broken screen, so it gets its own message.
   */
  const verifyCompany = useCallback(
    async (companyNumber?: string): Promise<DRCProfile | null> => {
      if (!userId) return null;
      try {
        setSavingTrue();
        const { data } = await verifyDRCCompanyRequest(userId, companyNumber);
        applyResponse(data);
        showNotification({ message: 'Company verified', options: { variant: 'success' } });
        return data.profile;
      } catch (e: any) {
        if (e?.response?.status === 404) {
          const attempted = companyNumber || profile?.company_number;
          showNotification({
            message: `Companies House has no company ${attempted}`,
            description: 'Check the number and try again.',
            options: { variant: 'error' },
          });
        } else {
          handleServerError(e);
        }
        return null;
      } finally {
        setSavingFalse();
      }
    },
    [
      userId,
      profile?.company_number,
      applyResponse,
      handleServerError,
      showNotification,
      setSavingTrue,
      setSavingFalse,
    ]
  );

  return {
    profile,
    status,
    loading,
    saving,
    vatChecking,
    refresh: getProfile,
    saveProfile,
    setEndUserStatus,
    recordIR35,
    setPeriod,
    verifyVat,
    verifyCompany,
  };
};

export type DRCProfileState = ReturnType<typeof useDRCProfile>;
